import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormDialog } from '@/components/shared/form-dialog';

import { BasicMemberFields, MoreMemberFields } from './member-form-fields';
import { emptyMemberForm, type MemberFormState } from './member-form-state';

export interface MemberCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Applied when the dialog opens. Snapshot only — later mutations are ignored. */
  initialValues?: Partial<MemberFormState>;
  /** Change this when opening for a different person so the form resets. */
  resetKey?: string;
  title?: string;
  description?: string;
  submitLabel?: string;
  submitting?: boolean;
  onSubmit: (form: MemberFormState) => void | Promise<void>;
}

export const MemberCreateDialog = ({
  open,
  onOpenChange,
  initialValues,
  resetKey,
  title,
  description,
  submitLabel,
  submitting = false,
  onSubmit,
}: MemberCreateDialogProps) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const [form, setForm] = useState<MemberFormState>(() => emptyMemberForm(initialValues));

  useEffect(() => {
    if (!open) return;
    setForm(emptyMemberForm(initialValues));
    // Snapshot the values present when this person/dialog is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resetKey]);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title ?? t('members.form.createTitle')}
      description={description}
      size="2xl"
      basicLabel={t('members.form.basicTab')}
      moreLabel={t('members.form.moreTab')}
      basic={<BasicMemberFields value={form} onChange={setForm} />}
      more={<MoreMemberFields value={form} onChange={setForm} />}
      submitLabel={submitLabel ?? t('members.form.save')}
      cancelLabel={tCommon('action.cancel')}
      closeLabel={tCommon('action.close')}
      submitting={submitting}
      onSubmit={async () => {
        await onSubmit(form);
      }}
    />
  );
};
